import { useState } from 'react';
import type { DrawingState, Tool } from '../hooks/useDrawing';
import { useT, useLang, setLang } from '../i18n';
import { LANGS } from '../i18n/translations';
import { Popover } from './Popover';

const COLORS = ['#ff2d55', '#ffd60a', '#34c759', '#0a84ff', '#bf5af2', '#ffffff', '#000000'];
const SIZES = [2, 4, 8, 14, 22];

type Props = {
  state: DrawingState;
  onStateChange: (s: DrawingState) => void;
  onUndo: () => void;
  canUndo: boolean;
  onClearPage: () => void;
  onClearAll: () => void;
  onExportPdf?: () => void;
  onExportPng: () => void;
  onClose: () => void;
  fileName: string;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
};

type MenuId = 'tool' | 'color' | 'size' | 'more' | 'lang' | null;

export function Toolbar({
  state,
  onStateChange,
  onUndo,
  canUndo,
  onClearPage,
  onClearAll,
  onExportPdf,
  onExportPng,
  onClose,
  fileName,
  zoom,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  isFullscreen = false,
  onToggleFullscreen,
}: Props) {
  const t = useT();
  const lang = useLang();
  const [open, setOpen] = useState<MenuId>(null);

  const setTool = (tool: Tool) => onStateChange({ ...state, tool });
  const setColor = (color: string) => onStateChange({ ...state, color, tool: 'pen' });
  const setSize = (size: number) => onStateChange({ ...state, size });
  const close = () => setOpen(null);
  const toggle = (id: Exclude<MenuId, null>) => setOpen((cur) => (cur === id ? null : id));

  return (
    <div className="toolbar">
      <button className="tb-icon" onClick={onClose} title={t('tool.close')} aria-label={t('tool.close')}>
        ✕
      </button>
      <a
        className="tb-icon tb-games"
        href={`https://games.smarty.center/?lang=${lang}`}
        title={t('nav.games')}
        aria-label={t('nav.games')}
      >
        🎮
      </a>
      <span className="tb-filename" title={fileName}>{fileName}</span>

      {/* Инструмент: pen / eraser */}
      <Popover
        open={open === 'tool'}
        onClose={close}
        align="start"
        trigger={
          <button className="tb-icon" onClick={() => toggle('tool')} aria-label={t('tool.pen')}>
            {state.tool === 'pen' ? '✎' : '⌫'}
          </button>
        }
      >
        <button
          className={`tb-menu-item ${state.tool === 'pen' ? 'active' : ''}`}
          onClick={() => { setTool('pen'); close(); }}
        >
          <span className="tb-mi-icon">✎</span> {t('tool.pen')}
        </button>
        <button
          className={`tb-menu-item ${state.tool === 'eraser' ? 'active' : ''}`}
          onClick={() => { setTool('eraser'); close(); }}
        >
          <span className="tb-mi-icon">⌫</span> {t('tool.eraser')}
        </button>
      </Popover>

      {/* Цвет */}
      <Popover
        open={open === 'color'}
        onClose={close}
        align="start"
        trigger={
          <button
            className="tb-swatch"
            style={{ background: state.color }}
            onClick={() => toggle('color')}
            aria-label={state.color}
            title={state.color}
          />
        }
      >
        <div className="tb-color-grid">
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => { setColor(c); close(); }}
              className={`color-dot ${state.color === c && state.tool === 'pen' ? 'active' : ''}`}
              style={{ background: c }}
              aria-label={c}
            />
          ))}
        </div>
      </Popover>

      {/* Размер пера */}
      <Popover
        open={open === 'size'}
        onClose={close}
        align="start"
        trigger={
          <button
            className="tb-icon tb-size-trigger"
            onClick={() => toggle('size')}
            aria-label={String(state.size)}
            title={String(state.size)}
          >
            <span
              className="size-preview"
              style={{ width: state.size + 4, height: state.size + 4, background: state.color }}
            />
          </button>
        }
      >
        <div className="tb-size-list">
          {SIZES.map((s) => (
            <button
              key={s}
              onClick={() => { setSize(s); close(); }}
              className={`size-dot ${state.size === s ? 'active' : ''}`}
              aria-label={String(s)}
            >
              <span
                className="size-preview"
                style={{ width: s + 4, height: s + 4, background: state.color }}
              />
            </button>
          ))}
        </div>
      </Popover>

      {/* Зум + fullscreen */}
      <div className="tb-zoom">
        <button onClick={onZoomOut} title={t('tool.zoomOut')} aria-label={t('tool.zoomOut')}>−</button>
        <button onClick={onZoomReset} title={t('tool.zoomReset')} className="zoom-label">
          {Math.round(zoom * 100)}%
        </button>
        <button onClick={onZoomIn} title={t('tool.zoomIn')} aria-label={t('tool.zoomIn')}>+</button>
        {onToggleFullscreen && (
          <button
            onClick={onToggleFullscreen}
            title={isFullscreen ? t('tool.exitFullscreen') : t('tool.fullscreen')}
            aria-label={isFullscreen ? t('tool.exitFullscreen') : t('tool.fullscreen')}
          >
            {isFullscreen ? '⤓' : '⛶'}
          </button>
        )}
      </div>

      {/* Меню "Ещё" — Undo / Clear / Export */}
      <Popover
        open={open === 'more'}
        onClose={close}
        align="end"
        trigger={
          <button className="tb-icon" onClick={() => toggle('more')} aria-label="⋯">⋯</button>
        }
      >
        <button className="tb-menu-item" disabled={!canUndo} onClick={() => { onUndo(); close(); }}>
          <span className="tb-mi-icon">↶</span> {t('tool.undo')}
        </button>
        <button className="tb-menu-item" onClick={() => { onClearPage(); close(); }}>
          <span className="tb-mi-icon">⌫</span> {t('tool.clearPage')}
        </button>
        <button className="tb-menu-item tb-menu-danger" onClick={() => { onClearAll(); close(); }}>
          <span className="tb-mi-icon">🗑</span> {t('tool.clearAll')}
        </button>
        <div className="tb-menu-sep" />
        <button className="tb-menu-item" onClick={() => { onExportPng(); close(); }}>
          <span className="tb-mi-icon">⬇</span> {t('tool.exportPng')}
        </button>
        {onExportPdf && (
          <button className="tb-menu-item" onClick={() => { onExportPdf(); close(); }}>
            <span className="tb-mi-icon">⬇</span> {t('tool.exportPdf')}
          </button>
        )}
      </Popover>

      {/* Язык */}
      <Popover
        open={open === 'lang'}
        onClose={close}
        align="end"
        className="tb-lang"
        trigger={
          <button className="tb-icon tb-lang-trigger" onClick={() => toggle('lang')} aria-label={t('lang.label')}>
            {lang.toUpperCase()}
          </button>
        }
      >
        {LANGS.map((l) => (
          <button
            key={l.code}
            className={`tb-menu-item ${l.code === lang ? 'active' : ''}`}
            onClick={() => { setLang(l.code); close(); }}
          >
            {l.label}
          </button>
        ))}
      </Popover>
    </div>
  );
}
